import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, mergeProps as _mergeProps, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "c-gen-nav__desktop-menus" }
const _hoisted_2 = { class: "c-gen-nav__menus" }
const _hoisted_3 = ["onMouseenter"]
const _hoisted_4 = {
  key: 0,
  class: "c-gen-nav__mega-menu"
}
const _hoisted_5 = { class: "c-gen-nav__mega-menu-inner" }
const _hoisted_6 = { class: "c-gen-nav__mega-media" }
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = { class: "c-gen-nav__mega-columns" }
const _hoisted_9 = { class: "c-gen-nav__mega-column" }
const _hoisted_10 = {
  key: 0,
  class: "c-gen-nav__mega-column"
}

import { computed, ref } from 'vue';
import utils from '@/utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import * as querystring from 'querystring';
import { HeaderProps } from '@/types/components/Header';
import SiteLink from '@/elements/SiteLink.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'HeaderDesktop',
  props: {
    page: {},
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const menus = computed(() => props.fields?.menus ?? []);

const activeMenuTitle = ref('');
const hoveredCategory = ref('');
const hoveredItem = ref('');
let leaveTimer: ReturnType<typeof setTimeout> | null = null;

const onMenuEnter = (menu: any) => {
  if (leaveTimer) {
    clearTimeout(leaveTimer);
    leaveTimer = null;
  }

  activeMenuTitle.value = utils.sc.getFieldValue(menu, 'title');

  const firstCategory = getCategories(menu)[0];
  hoveredCategory.value = firstCategory ? getTitle(firstCategory) : '';

  const firstItem = firstCategory ? getItems(firstCategory)[0] : null;
  hoveredItem.value = firstItem ? getTitle(firstItem) : '';
};

const onMenuLeave = () => {
  leaveTimer = setTimeout(() => {
    activeMenuTitle.value = '';
    hoveredCategory.value = '';
    hoveredItem.value = '';
  }, 150);
};

const activeMenu = computed(() =>
  menus.value.find((menu) => utils.sc.getFieldValue(menu, 'title') === activeMenuTitle.value)
);

const activeCategory = computed(() =>
  getCategories(activeMenu.value).find((cat) => getTitle(cat) === hoveredCategory.value)
);

const activeCategoryItems = computed(() => getItems(activeCategory.value));

const activeItem = computed(() =>
  activeCategoryItems.value.find((item) => getTitle(item) === hoveredItem.value)
);

const activeSubItems = computed(() => getSubItems(activeItem.value));

const setHoveredCategory = (cat: any) => {
  hoveredCategory.value = getTitle(cat);

  const firstItem = getItems(cat)[0];
  hoveredItem.value = firstItem ? getTitle(firstItem) : '';
};

const setHoveredItem = (item: any) => {
  hoveredItem.value = getTitle(item);
};

const getCategories = (menu: any) => {
  const f = menu?.fields || menu || {};
  const c = f.categories;
  return Array.isArray(c) ? c : [];
};

const hasCategories = (menu: any) => {
  return getCategories(menu).length > 0;
};

const getItems = (cat: any) => {
  const f = cat?.fields || cat || {};
  const i = f.items;
  return Array.isArray(i) ? i : [];
};

const hasItems = (cat: any) => {
  return getItems(cat).length > 0;
};

const getSubItems = (item: any) => {
  const f = item?.fields || item || {};
  const s = f.subItems;
  return Array.isArray(s) ? s : [];
};

const hasSubItems = (item: any) => {
  return getSubItems(item).length > 0;
};

const getTitle = (item: any) => {
  const title = utils.sc.getFieldObj(item, 'title');
  return title?.value || title || item?.displayName || item?.name || '';
};

const getLinkHref = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  let url = link?.value?.href || link?.href || '';

  if (!url) return '';
  if (/^(mailto|tel):/gi.test(url)) return url;
  if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) return url.replace(/^https?:\/\//gi, '');

  const queryString = link?.value?.querystring || link?.querystring;
  if (queryString) {
    try {
      url = appendQuery(url, querystring.parse(queryString));
    } catch (error) {
      console.warn('Failed to parse querystring:', queryString, error);
    }
  }

  const anchor = link?.value?.anchor || link?.anchor;
  if (anchor) {
    url = `${url}?id=${anchor}`;
  }

  return getBetterUrl(url) ?? '';
};

const hasLink = (item: any) => Boolean(getLinkHref(item));

const getLinkField = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  return {
    value: {
      ...(link?.value || link || {}),
      href: link?.value?.href || link?.href || '',
    },
  };
};

const onLinkClick = (event: MouseEvent, item: any) => {
  if (hasLink(item)) return;
  event.preventDefault();
};

const getLinkAttrs = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  const value = link?.value || link || {};
  const attrs: Record<string, string> = {};

  if (!hasLink(item)) attrs['aria-disabled'] = 'true';
  if (value.target && hasLink(item)) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.class) attrs.class = value.class;
  if (value.target === '_blank' && hasLink(item)) attrs.rel = 'noopener noreferrer';

  return attrs;
};

const getMegaImage = (menu: any) => utils.sc.getFieldObj(menu, 'megaImage');

const getMegaImageSrc = (menu: any) => {
  const image = getMegaImage(menu);
  return image?.value?.src || image?.src || '';
};

const getMegaImageAlt = (menu: any) => {
  const image = getMegaImage(menu);
  return image?.value?.alt || image?.alt || '';
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("ul", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(menus.value, (menu, i) => {
        return (_openBlock(), _createElementBlock("li", {
          key: i,
          class: "c-gen-nav__menu-item",
          onMouseenter: ($event: any) => (onMenuEnter(menu)),
          onMouseleave: onMenuLeave
        }, [
          _createVNode(SiteLink, _mergeProps({
            class: ["c-gen-nav__menu-link", {
            'is-active': activeMenuTitle.value === _unref(utils).sc.getFieldValue(menu, 'title'),
            'is-disabled': !hasLink(menu),
          }],
            link: getLinkField(menu)
          }, { ref_for: true }, getLinkAttrs(menu), {
            onClick: ($event: any) => (onLinkClick($event, menu))
          }), {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(utils).sc.getFieldValue(menu, 'title')), 1)
            ]),
            _: 2
          }, 1040, ["class", "link", "onClick"]),
          _createVNode(_Transition, { name: "slide-down" }, {
            default: _withCtx(() => [
              (hasCategories(menu) && activeMenuTitle.value === _unref(utils).sc.getFieldValue(menu, 'title'))
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, [
                        (getMegaImageSrc(menu))
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: getMegaImageSrc(menu),
                              alt: getMegaImageAlt(menu),
                              loading: "lazy"
                            }, null, 8, _hoisted_7))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getCategories(menu), (cat) => {
                            return (_openBlock(), _createBlock(SiteLink, _mergeProps({
                              key: _unref(utils).sc.getFieldValue(cat, 'title'),
                              class: ["c-gen-nav__mega-card", {
                      'is-active': hoveredCategory.value === getTitle(cat),
                      'is-disabled': !hasLink(cat),
                    }],
                              link: getLinkField(cat)
                            }, { ref_for: true }, getLinkAttrs(cat), {
                              onClick: ($event: any) => (onLinkClick($event, cat)),
                              onMouseenter: ($event: any) => (setHoveredCategory(cat)),
                              onFocus: ($event: any) => (setHoveredCategory(cat))
                            }), {
                              default: _withCtx(() => [
                                _createElementVNode("span", null, _toDisplayString(getTitle(cat)), 1),
                                (hasItems(cat))
                                  ? (_openBlock(), _createBlock(_component_Icon, {
                                      key: 0,
                                      name: "arrow-right",
                                      class: "c-gen-nav__icon c-gen-nav__mega-card-arrow"
                                    }))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1040, ["class", "link", "onClick", "onMouseenter", "onFocus"]))
                          }), 128))
                        ]),
                        (activeCategoryItems.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeCategoryItems.value, (item) => {
                                return (_openBlock(), _createBlock(SiteLink, _mergeProps({
                                  key: _unref(utils).sc.getFieldValue(item, 'title'),
                                  class: ["c-gen-nav__mega-card", {
                      'is-active': hoveredItem.value === getTitle(item),
                      'is-disabled': !hasLink(item),
                    }],
                                  link: getLinkField(item)
                                }, { ref_for: true }, getLinkAttrs(item), {
                                  onClick: ($event: any) => (onLinkClick($event, item)),
                                  onMouseenter: ($event: any) => (setHoveredItem(item)),
                                  onFocus: ($event: any) => (setHoveredItem(item))
                                }), {
                                  default: _withCtx(() => [
                                    _createElementVNode("span", null, _toDisplayString(getTitle(item)), 1),
                                    (hasSubItems(item))
                                      ? (_openBlock(), _createBlock(_component_Icon, {
                                          key: 0,
                                          name: "arrow-right",
                                          class: "c-gen-nav__icon c-gen-nav__mega-card-arrow"
                                        }))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _: 2
                                }, 1040, ["class", "link", "onClick", "onMouseenter", "onFocus"]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", {
                          class: _normalizeClass(["c-gen-nav__mega-column", { 'is-empty': !activeSubItems.value.length }])
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeSubItems.value, (sub) => {
                            return (_openBlock(), _createBlock(SiteLink, _mergeProps({
                              key: _unref(utils).sc.getFieldValue(sub, 'title'),
                              class: ["c-gen-nav__mega-link", { 'is-disabled': !hasLink(sub) }],
                              link: getLinkField(sub)
                            }, { ref_for: true }, getLinkAttrs(sub), {
                              onClick: ($event: any) => (onLinkClick($event, sub))
                            }), {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(getTitle(sub)), 1)
                              ]),
                              _: 2
                            }, 1040, ["class", "link", "onClick"]))
                          }), 128))
                        ], 2)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1024)
        ], 40, _hoisted_3))
      }), 128))
    ])
  ]))
}
}

})